/**
 * UsersList Component
 *
 * This component is responsible for rendering a list of users.
 * It receives `tableHeaders` and `tableData` as props, which are used to display the users table.
 *
 * It uses a local state `showNewUserModal` to control the visibility of the `AddNewUserModal` component.
 * The `handleNewUserModalClose` and `handleNewUserModalShow` functions are used to close and open the modal respectively.
 * The `handleNewUserModalSubmit` function is used to handle the submission of the new user form in the modal.
 *
 * The `AddNewUserModal` component is rendered with the `showNewUserModal`, `handleNewUserModalClose`, and `handleNewUserModalSubmit` passed as props.
 *
 * The `SettingsListingTable` component is used to display the users table with the provided headers and data.
 *
 * @component
 */

import React, { useState } from "react";
import { Button, FloatingLabel, FormSelect } from "react-bootstrap";
import { UserAddOutlined } from "../../../../helpers/icons/antDesignIcons";
import "../CompanySettingsPage.scss";
import AddNewUserModal from "./AddNewUserModal";
import { addNewCompanyUser } from "../../../../api/companySettingsApi";
import SettingsListingTable from "../../Shared/SettingsListingTable/SettingsListingTable";
import { toast } from "react-toastify";
import useTranslation from "../../../../hooks/useTranslation";
import USER_ROLES from "../../../../constants/userRoles";

const UsersList = ({
  tableHeaders,
  tableData,
  onChange,
  isUserSuperAdmin,
  companyList,
}) => {
  const { t } = useTranslation("settingsPage");
  const [showNewUserModal, setShowNewUserModal] = useState(false);

  const handleNewUserModalShow = () => setShowNewUserModal(true);
  const handleNewUserModalClose = () => setShowNewUserModal(false);
  const handleNewUserModalSubmit = async (formData) => {
    // Check if any required field is empty
    const requiredFields = [
      "lastname",
      "firstname",
      "email",
      "phonenumber",
      "language",
      "roles",
    ];
    const emptyFields = requiredFields.filter((field) => !formData[field]);

    if (emptyFields.length > 0) {
      toast.error(t("starSignedFields"));
      return;
    }

    try {
      // If no errors, proceed with form submission
      const response = await addNewCompanyUser(formData);

      if (!response.success) {
        if (response.details) {
          // Display validation errors
          Object.values(response.details).forEach((error) => {
            toast.error(error);
          });
        } else {
          // Display general error message
          toast.error(response.message);
        }
        return;
      }

      // Display success toast
      toast.success(t("add_new_user_success"));

      // Close the modal
      handleNewUserModalClose();
    } catch (error) {
      console.error("Unexpected error during user creation:", error);
      toast.error(t("unexpected_error"));
    }
  };

/*   console.log("IS USER SUPER ADMIN:", isUserSuperAdmin);
  console.log(
    "Rendering UsersList with headers:",
    tableHeaders,
    "and data:",
    tableData
  ); */

  // Filter out superadmin users if the current user is not a superadmin
  if (isUserSuperAdmin === false) {
    tableData = tableData.filter(
      (user) => user.roles !== USER_ROLES.SUPER_ADMIN
    );
  }

  return (
    <>
      <div className="company-settings-page__list">
        <div className="company-settings-page__list__header">
          <h2>{t("users")}</h2>
          <Button
            onClick={handleNewUserModalShow}
            className="company-settings-page__list__header__add-button"
          >
            <UserAddOutlined />
          </Button>
          <AddNewUserModal
            showNewUserModal={showNewUserModal}
            handleNewUserModalClose={handleNewUserModalClose}
            handleNewUserModalSubmit={handleNewUserModalSubmit}
          />
        </div>
        {/* Select company dropdown for superadmin */}
        {isUserSuperAdmin && (
          <div className="company-settings-page__list__company-select">
            <FloatingLabel controlId="floatingSelect" label="Cég kiválasztása">
              <FormSelect onChange={(e) => onChange(e.target.value)}>
                <option value="all">Összes cég</option>
                {companyList.map((company) => (
                  <option key={company.companyid} value={company.companyid}>
                    {company.companyname}
                  </option>
                ))}
              </FormSelect>
            </FloatingLabel>
          </div>
        )}
        <div className="company-settings-page__list__content table-wrapper">
          <SettingsListingTable
            tableHeaders={tableHeaders}
            tableData={tableData}
            tableType="user"
          />
        </div>
      </div>
    </>
  );
};

export default UsersList;
